import type {
  AccessMode,
  AdditionalPersistedCopy,
  OperationEffect,
  RiskClassification,
  SubscriptionOperationProfile,
  WriteBack,
} from './consumerContractRegistry.types';

/** Evidence-based operation profiles — operation risk only, not change propagation. */
export const OPERATION_PRESETS = {
  summaryRowIndirectReadFormat: {
    accessMode: 'indirect_read_shared_builder',
    operation: 'read_and_format',
    writeBack: 'none',
    additionalPersistedCopy: 'none',
    riskClassification: 'expected_neutral',
    riskRationale:
      'Render-time read through shared summary builder; formats label/value for display or export only. Does not mutate meta_json or persist an additional editable copy.',
  },
  publicPdfMapperReadFormat: {
    accessMode: 'indirect_read_shared_builder',
    operation: 'read_and_format',
    writeBack: 'none',
    additionalPersistedCopy: 'none',
    riskClassification: 'expected_neutral',
    riskRationale:
      'Read and format via Public PDF mapper in the Edge Function. No write-back or additional persisted copy verified in this path.',
  },
  summaryBuilderAggregate: {
    accessMode: 'indirect_read_shared_builder',
    operation: 'read_and_format',
    writeBack: 'none',
    additionalPersistedCopy: 'none',
    riskClassification: 'expected_neutral',
    riskRationale:
      'Shared builder produces ephemeral label/value rows for presentation. Field-level subscriptions document each decomposed output.',
  },
  publicPdfMapperAggregate: {
    accessMode: 'indirect_read_shared_builder',
    operation: 'read_and_format',
    writeBack: 'none',
    additionalPersistedCopy: 'none',
    riskClassification: 'expected_neutral',
    riskRationale:
      'Public PDF mapper aggregate output. Operation is read-and-format only; propagation is tracked separately.',
  },
  directReadFormat: {
    accessMode: 'direct_read',
    operation: 'read_and_format',
    writeBack: 'none',
    additionalPersistedCopy: 'none',
    riskClassification: 'expected_neutral',
    riskRationale:
      'Direct read from declared source path with formatting for display or export. No write-back to meta_json in this artifact path.',
  },
  directReadOnly: {
    accessMode: 'direct_read',
    operation: 'read_only',
    writeBack: 'none',
    additionalPersistedCopy: 'none',
    riskClassification: 'expected_neutral',
    riskRationale: 'Direct read without transformation. No write-back in this artifact path.',
  },
  storedPngCachedRead: {
    accessMode: 'cached_derivative',
    operation: 'read_only',
    writeBack: 'none',
    additionalPersistedCopy: 'derived_cache',
    riskClassification: 'expected_neutral',
    riskRationale:
      'Reads pre-generated PNG URL from meta_json preview paths. Stored PNG is a cached derivative, not canonical authority.',
  },
  inMemoryCalculate: {
    accessMode: 'derived_calculated',
    operation: 'calculate_derive',
    writeBack: 'none',
    additionalPersistedCopy: 'none',
    riskClassification: 'expected_neutral',
    riskRationale:
      'Derives in-memory projection from source fields. No meta_json write-back verified in this subscription path.',
  },
  inMemoryCalculateDriftRisk: {
    accessMode: 'derived_calculated',
    operation: 'calculate_derive',
    writeBack: 'none',
    additionalPersistedCopy: 'none',
    riskClassification: 'drift_risk',
    riskRationale:
      'Verified competing merge inputs (e.g. blank dimension strings on quote card vs specs on export) can produce different effective payloads without mutating source.',
  },
  legacyKvFallback: {
    accessMode: 'direct_read',
    operation: 'read_and_format',
    writeBack: 'none',
    additionalPersistedCopy: 'none',
    riskClassification: 'expected_neutral',
    riskRationale:
      'Legacy flat KV fallback when summaryRows empty. Read-and-format only; separate propagation path documented independently.',
  },
  fieldOmittedSanitize: {
    accessMode: 'direct_read',
    operation: 'read_only',
    writeBack: 'none',
    additionalPersistedCopy: 'none',
    riskClassification: 'expected_neutral',
    riskRationale:
      'Field excluded from public payload by sanitizeVisualMeta whitelist. Read path documents omission, not consumption.',
  },
  canonicalPayloadRead: {
    accessMode: 'direct_read',
    operation: 'read_only',
    writeBack: 'none',
    additionalPersistedCopy: 'none',
    riskClassification: 'expected_neutral',
    riskRationale:
      'Authoritative canonical templatePayload reference. This path reads geometry without mutating templatePayload.',
  },
  compatibilityOverlayCalculate: {
    accessMode: 'derived_calculated',
    operation: 'calculate_derive',
    writeBack: 'none',
    additionalPersistedCopy: 'none',
    riskClassification: 'expected_neutral',
    riskRationale:
      'In-memory compatibility overlay merges specs onto templatePayload. Operation does not write back; overlay drift is tracked via change propagation.',
  },
  cardBlankDimensionMerge: {
    accessMode: 'derived_calculated',
    operation: 'calculate_derive',
    writeBack: 'none',
    additionalPersistedCopy: 'none',
    riskClassification: 'expected_neutral',
    riskRationale:
      'Legacy compact-card path only: mergeModalStateIntoPayload with blank widthMm/heightMm overlays presentation fields while preserving payload geometry. Current v2_lite lines with saved templatePayload bypass this resolver.',
  },
  modalWorkingPayloadSync: {
    accessMode: 'derived_calculated',
    operation: 'calculate_derive',
    writeBack: 'none',
    additionalPersistedCopy: 'none',
    riskClassification: 'expected_neutral',
    riskRationale:
      'Modal form fields (including widthMm/heightMm from buildModalMergeStateFromForm) sync into workingPayload via mergeModalStateIntoPayload. Same workingPayload feeds Preview, Configure, Save, and PNG generation within the session.',
  },
  modalPreviewRender: {
    accessMode: 'direct_read',
    operation: 'read_only',
    writeBack: 'none',
    additionalPersistedCopy: 'none',
    riskClassification: 'expected_neutral',
    riskRationale:
      'Renders workingPayload (or v2PayloadToUse fallback) via WindowGeneratorSvg without a separate merge at preview time (payloadToRender).',
  },
} as const satisfies Record<string, SubscriptionOperationProfile>;

export type OperationPresetKey = keyof typeof OPERATION_PRESETS;

export function operationPreset(key: OperationPresetKey): SubscriptionOperationProfile {
  return { ...OPERATION_PRESETS[key] };
}

export function isValidAccessMode(value: string): value is AccessMode {
  return (
    value === 'direct_read' ||
    value === 'indirect_read_shared_builder' ||
    value === 'derived_calculated' ||
    value === 'cached_derivative'
  );
}

export function isValidOperationEffect(value: string): value is OperationEffect {
  return (
    value === 'read_only' ||
    value === 'read_and_format' ||
    value === 'calculate_derive' ||
    value === 'create_cached_representation' ||
    value === 'persist_duplicate' ||
    value === 'mutate_source'
  );
}

export function isValidWriteBack(value: string): value is WriteBack {
  return value === 'none' || value === 'writes_derivative_cache_only' || value === 'writes_source';
}

export function isValidAdditionalPersistedCopy(value: string): value is AdditionalPersistedCopy {
  return value === 'none' || value === 'derived_cache' || value === 'independently_editable_copy';
}

export function isValidRiskClassification(value: string): value is RiskClassification {
  return (
    value === 'expected_neutral' ||
    value === 'attention' ||
    value === 'drift_risk' ||
    value === 'conflicting_authority'
  );
}
